import { Api, ContentType } from "./Api";

/**
 * HTTP header values are byte strings, so fetch() refuses to build a request holding any code
 * point above U+00FF: "Failed to read the 'headers' property from 'RequestInit': String contains
 * non ISO-8859-1 code point". That kills the upload in the browser before it reaches the backend,
 * for Cyrillic and CJK names but also for the far more common en dash in a name pasted out of Word.
 *
 * So the file name goes out twice: a lossy ASCII copy in the plain slots, and the real UTF-8 name
 * RFC 5987 encoded in filename*, which is what the backend stores (AttachmentFileNameHelper).
 */
const toAsciiFallback = (fileName: string) =>
  fileName.replace(/["\\]/g, "_").replace(/[^\x20-\x7E]/g, "_");

// encodeURIComponent leaves ' ( ) * unescaped; RFC 5987 attr-char does not allow them.
const toRfc5987 = (fileName: string) =>
  encodeURIComponent(fileName).replace(
    /['()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`
  );

export const uploadAttachment = async (
  client: Api<unknown>,
  path: string,
  query: Record<string, string>,
  file: File
) => {
  const asciiName = toAsciiFallback(file.name);

  try {
    const response = await client.request<string, string>({
      path,
      query,
      method: "POST",
      secure: true,
      type: ContentType.Binary,
      format: "text",
      headers: {
        "X-File-Name": asciiName,
        "Content-Disposition": `attachment; filename="${asciiName}"; filename*=UTF-8''${toRfc5987(file.name)}`
      },
      body: file
    });
    return response.data;
  } catch (error) {
    // The client throws an HTTP response; the uploader displays an Error message.
    if (error instanceof Response) {
      const message = await error.text();
      throw new Error(message || "Upload failed");
    }
    throw error;
  }
};

export const downloadAttachment = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};
